import { FeatureSwitches, parseFeatureSwitches } from './featureSwitches';
import { createRefreshingCache } from './refreshingCache';

/**
 * Whether the switch row is answering yet.
 *
 * A caller must handle `active: false` as "the account's grants decide, less
 * any feature it holds the block of", never as an empty table — an empty table
 * closes every feature, which is the one outcome reading a row must not cause.
 */
export type FeatureSwitchTable =
  | { readonly active: true; readonly switches: FeatureSwitches }
  | { readonly active: false };

const INACTIVE: FeatureSwitchTable = { active: false };

/**
 * Fetches the `settings` value of the `feature_switches` config row, or
 * resolves `undefined` where no such row exists yet.
 */
export type FeatureSwitchesLoader = () => Promise<unknown>;

/**
 * Short, because this window is how long a kill switch takes to bite.
 *
 * The plan-feature caches either side of this package hold for an hour, which
 * is why the Cloudflare beta runbook has to quote "up to 1 hour" for closing
 * the beta on a plan. An hour-long kill switch is not one.
 */
export const DEFAULT_VALIDITY_WINDOW_MS = 30_000;

export type FeatureSwitchesCacheOptions = {
  readonly validityWindowMs?: number;
  readonly onRefreshFailure?: (error: Error) => void;
  readonly retryAfterFailureMs?: number;
};

export type FeatureSwitchesCache = {
  table: () => Promise<FeatureSwitchTable>;
};

export function createFeatureSwitchesCache(
  load: FeatureSwitchesLoader,
  options: FeatureSwitchesCacheOptions = {}
): FeatureSwitchesCache {
  const table = createRefreshingCache(
    async (): Promise<FeatureSwitchTable> => {
      const value = await load();

      return value === undefined ? INACTIVE : { active: true, switches: parseFeatureSwitches(value) };
    },
    // A first read that fails describes nothing, so it leaves the caller on the
    // behaviour it had before rather than closing every feature because the
    // database blinked.
    () => INACTIVE,
    {
      validityWindowMs: options.validityWindowMs ?? DEFAULT_VALIDITY_WINDOW_MS,
      onRefreshFailure: options.onRefreshFailure,
      retryAfterFailureMs: options.retryAfterFailureMs,
    }
  );

  return { table };
}
